<template>
	<div class="w-full h-full">
		
		<!-- Model List -->
		<div v-if="!Layout.loading && !currentModel" class="px-6 pb-4">

			<div v-if="!Layout.showTrainingMode" class="box px-3 py-5 shadow-none border border-solid border-gray-300 bg-gray-50">
				<div class="text-center">
					<span class="text-xs text-gray-500">
						{{ localModelsMenu.length ? $t('Keep fine-tuning the precision') : $t('Has not been trained') }}
					</span>
					<div class="text-center mt-2">
						<button type="button" @click="Layout.showTrainingMode = true" class="btn bg-green-600 text-white py-3 px-8">{{ $t('Train a new model') }}</button>
					</div>
					<div class="mt-3 text-gray-500 text-center">
						{{$t('If there are fewer Images, inaccurate accuracy and recall scores are likely to be generated')}}.<br />
						{{$t('Assign the maximum number of images to each tag before training')}}. 
					</div>
				</div>
			</div>

			<div v-else class="grid grid-cols-1 md:grid-cols-2 gap-6">
				
				<div @click="selectTrainingMode('production')" 
					class="group cursor-pointer transition-all duration-200 hover:shadow-lg">

					<div class="box p-10 text-center border border-gray-300 bg-gray-50 hover:bg-blue-50 transition-all duration-200">

						<h3 class="text-lg font-semibold text-gray-700 mb-3 transition-colors duration-200">
							{{ $t('Production Training') }}
						</h3>

						<p class="text-gray-600 text-sm leading-relaxed mb-6">
							{{ $t('Optimized real-time models built for production use. These models are lightweight, fast, and suitable for deployment where performance and responsiveness are critical.') }}
						</p>

						<button class="py-3 px-6 rp-bg-blue-1 text-white font-medium rounded-lg hover:rp-bg-blue-3 transition-colors duration-200 group-hover:rp-bg-blue-3">
							{{ $t('Select production mode') }}
						</button>

					</div>

				</div>

				<div @click="!isLabelingDisabled && selectTrainingMode('labeling')"
					:class="[ 'group transition-all duration-200', isLabelingDisabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : 'cursor-pointer hover:shadow-lg' ]">
					
					<div class="box relative p-10 text-center border border-gray-300 bg-gray-50 hover:bg-green-50 transition-all duration-200" :class="isLabelingDisabled ? '' : 'hover:bg-green-50'">

						<h3 class="text-lg font-semibold text-gray-700 mb-3 transition-colors duration-200">
							{{ $t('Labeling Assistant') }}
						</h3>

						<p class="text-gray-600 text-sm leading-relaxed mb-6">
							{{ $t('High-capacity models designed to assist with image labeling tasks. Ideal for generating accurate and consistent annotations, especially during dataset creation or refinement.') }}
						</p>

						<button class="py-3 px-6 text-white font-medium rounded-lg"
							:class="isLabelingDisabled
								? 'bg-gray-400 cursor-not-allowed'
								: 'bg-green-600 hover:bg-green-700 group-hover:bg-green-700'"
							:disabled="isLabelingDisabled">
							{{ $t('Select labeling mode') }}
						</button>

						<span
							v-if="isLabelingDisabled" class="absolute inset-x-0 bottom-2 mx-auto w-max text-[11px] leading-none text-red-400/80 italic pointer-events-none">
								{{ $t('Currently only available for Object Detection datasets') }}
						</span>

					</div>
				</div>

			</div>

			<div v-if="Dataset?.localModels.length" class="grid grid-cols-12 gap-3 mt-6 flex-shrink-0">

				<div v-for="model in localModelsMenu" :key="model.id" class="intro-y col-span-6" @click="loadModel(model.id)">
					
					<div class="box model-box shadow-none border-gray-300 hover:bg-gray-200 bg-gray-50 h-24">
						
						<div class="mr-auto">
							<div class="font-medium truncate text-gray-700">{{ model.id.toString().substring(0, 35) }}<span v-if="model.id.toString().length > 35">...</span></div>
							<div class="text-gray-600 text-xs mt-0.5">{{ model.created }}</div>
						</div>

						<div v-if="model?.service && model?.service === 'visionPlatform'" class="flex items-center space-x-2">
							<div class="py-0.5 px-4 rounded-full text-xs cursor-pointer font-normal border border-gray-400 text-gray-500">
								{{ "VP" }}
							</div>
							<div v-if="model?.status === 'training' || model?.status === 'pending'" class="py-0.5 px-4 rounded-full text-xs cursor-pointer font-normal text-gray-500">
								<LoadingIcon icon="puff" class="w-6 h-6" />
							</div>
							<div v-else-if="model?.status === 'finished'" class="py-0.5 px-4 rounded-full text-xs cursor-pointer font-normal border border-green-400 text-green-500">
								{{ "Finished" }}
							</div>
							<div v-else class="py-0.5 px-4 rounded-full text-xs cursor-pointer font-normal border border-red-400 text-red-500">
								{{ "Failed" }}
							</div>
						</div>

						<div v-else>
							<div v-if="!model?.automl && model?.trainOperation?.operationId" class="py-0.5 px-4 rounded-full text-xs cursor-pointer font-normal text-gray-500"><LoadingIcon icon="puff" class="w-24 h-24" /></div>
							<div v-else class="py-0.5 px-4 rounded-full text-xs cursor-pointer font-normal border border-gray-400 text-gray-500">{{ model.aws ? "Anomaly detection" : "Supervised" }}</div>
						</div>

					</div>

				</div>

			</div>

		</div>
		
		<!-- Current Model -->
		<div v-else-if="!Layout.loading && currentModel && !showTrainingModal" class="h-full px-6 pt-6 pb-4 hidden-scrollbar overflow-y-auto">

			<div class="col-span-12">
				<div class="pb-2 text-left">

					<div class="w-full">

						<!-- Header -->
						<div class="grid chat grid-cols-12 gap-5 py-2 mt-2">
							<div class="col-span-12 text-left">
							
								<span class="text-xl truncate text-gray-700"> {{ currentModel?.db?.id }}</span>

								<span v-if="currentModel?.db?.aws" class="py-0.5 px-2 rounded-full text-xs font-normal ml-2 border border-gray-400 text-gray-500">{{ "Semi-supervised" }}</span>
								<span v-else-if="currentModel?.db?.automl" class="py-0.5 px-2 rounded-full text-xs font-normal ml-2 border border-gray-400 text-gray-500">{{ "Supervised" }}</span>
								<span v-else-if="currentModel?.db?.service === 'visionPlatform'" class="py-0.5 px-2 rounded-full text-xs font-normal ml-2 border border-gray-400 text-gray-500">{{ "Vision Platform" }}</span>

								<span class="py-2 px-6 rounded-full text-xs font-normal align-top text-center cursor-pointer float-right ml-auto bg-gray-700 text-white" @click="closeCurrentModel">{{ $t('Back') }}</span>
								<span class="py-2 px-3 rounded-full text-xs font-normal align-top text-center cursor-pointer float-right ml-auto bg-gray-500 hover:bg-red-500 text-white mr-2" @click="modal('model-delete-box')"><Trash2Icon class="w-4 h-4" /></span>
								
								<!-- Retrain Vertex -->
								<span class="py-2 px-2 rounded-full text-xs font-normal align-middle border text-center w-20 cursor-pointer float-right ml-auto mr-2 border-green-200 text-green-500" 
									v-if="currentModel?.db?.automl && currentModel.vertexEvaluation?.evaluations && currentModel.vertexEvaluation?.evaluations[0]?.model?.eligibleAsBaseModel" @click="modal('modal-trainBox')">
									{{ $t('Retrain') }}
								</span>
								
								<!-- Retrain AWS -->
								<span class="py-2 px-2 rounded-full text-xs font-normal align-middle border text-center w-20 cursor-pointer float-right ml-auto mr-2 border-green-200 text-green-500" 
									v-else-if="currentModel?.db?.aws" @click="modal('modal-trainBox')">
									{{ $t('Retrain') }}
								</span>
							
								<!-- Evaluation AWS -->
								<span v-if="currentModel?.db?.aws && currentModel?.awsEvaluation?.manifestResume && !semiSupervisedEvaluationObjects?.length && !currentModel.awsEvaluation?.loading" @click="loadEvaluationObjects(currentModel?.awsEvaluation?.manifest)" 
									class="py-2 px-2 rounded-full text-xs font-normal align-middle border border-rp-blue-1 text-rp-blue-1 w-48 text-center cursor-pointer hover:rp-bg-blue-1 hover:text-white float-right ml-auto mr-2">
									{{ $t('View evaluation images') }} ({{ currentModel.awsEvaluation.manifestResume?.totalTestImages }}) 
								</span>

								<!-- Metrics AWS -->
								<span v-else-if="currentModel?.db?.aws" @click="semiSupervisedEvaluationObjects=[]"
									class="py-2 px-2 rounded-full text-xs font-normal align-middle border text-center w-48 float-right ml-auto mr-2"
									:class="semiSupervisedEvaluationObjects?.length && !currentModel.awsEvaluation?.loading ? 'border-rp-blue-1 text-rp-blue-1 hover:rp-bg-blue-1 hover:text-white cursor-pointer' : 'border-gray-200 text-gray-500 cursor-not-allowed'">
									{{ $t('View Metrics') }}
								</span>

								<!-- Toggle Metrics Vision Platform -->
								<template v-if="currentModel?.db?.evaluation?.service === 'visionPlatform'">
									
									<button @click="toggleAnnotations" 
										class="metricsButton px-4 py-1" 
										:class="{ 'hover-enabled': Layout.annotationsLoaded && Object.keys(Layout.annotationsImages).length > 0 }"
										:disabled="!Layout.annotationsLoaded || Object.keys(Layout.annotationsImages).length === 0">

										<template v-if="!Layout.annotationsLoaded">
											<LoadingIcon icon="three-dots" class="w-5 h-5" />
										</template>

										<template v-else-if="Layout.annotationsImages && Object.keys(Layout.annotationsImages).length">
											<template v-if="!Layout.showAnnotations">
												{{ $t('View evaluation images') }} ({{ Object.keys(Layout.annotationsImages).length }})
											</template>
											<template v-else>
												{{ $t('View metrics') }}
											</template>
										</template>

										<template v-else>
											{{ $t('View evaluation images') }} 
										</template>

									</button>

								</template>

							</div>
						</div>
		
						<!-- Table settings -->
						<div v-if="currentModel.db" class="col-span-12">
							<table class="table table--sm text-xs text-left">
								<tbody>
									<tr v-if="currentModel.db.description">
										<td class="border"><span>{{$t('description')}}</span></td>
										<td class="border"><span>{{  currentModel.db.description }}</span></td>
									</tr>
								</tbody>
							</table>
						</div>

						<!-- New model training -->
						<div v-if="modelIsTraining" class="fixed top-0 left-0 w-full h-full bg-white text-black p-10 flex flex-col items-center justify-center" style="z-index: 999; background-color: #f2f3f3;">
							
							<TrainModelModal 
								:trainingStatus="currentModel?.db?.status"
								:modelId="currentModel?.db?.id"
								:trainingLog="currentModel?.db?.trainingLog"
								@trainFinished="closeTrainingModal; loadModel($event)"
								@closeModal="closeTrainingModal"/>
						</div>
		
						<!-- Vertex Results (Supervised) -->
						<div v-if="currentModel?.db?.automl" class="grid chat grid-cols-12 gap-6">

							<div class="col-span-5 text-center mt-8">

								<!-- Evaluation Error -->
								<div class="flex flex-col justify-center items-center" v-if="!currentModel?.vertexEvaluation?.render">
									<div class="intro-y col-span-12 lg:col-span-12 flex flex-col justify-center items-center" v-if="currentModel?.vertexEvaluation?.error">
										<div class="mt-3 text-gray-600 text-xs">{{ currentModel?.vertexEvaluation?.error }}</div>
									</div>
									<div class="intro-y col-span-12 lg:col-span-12 flex flex-col justify-center items-center" v-else>
										<LoadingIcon icon="puff" class="w-20 h-20" />
									</div>
								</div>

								<!-- Evaluation Diagram -->
								<div v-else>

									<div class="relative" v-if="currentModel.vertexEvaluation?.evaluations[0]">
										
										<!-- Donut Diagram -->
										<ReportDonutChart
											:height="190"
											:type="'doughnut'"
											:options="{
												responsive: true,
												maintainAspectRatio: false,
												cutout: '70%',
												plugins: {
													legend: { display: false }
												}
											}"
											:data="{
												labels: ['Train', 'Validation'],
												datasets: [{
													data: [
														currentModel.vertexEvaluation?.evaluations[0]?.model?.trainingDataItemsCount || 0,
														currentModel.vertexEvaluation?.evaluations[0]?.model?.validationDataItemsCount || 0
													],
													backgroundColor: ['#333333', '#1d8102']
												}]
											}"
										/>

										<!-- Total images -->
										<div class="flex flex-col justify-center items-center absolute w-full h-full top-0 left-0">
											<div class="text-xl xxl:text-2xl font-medium">
												{{ 
													parseInt(currentModel.vertexEvaluation?.evaluations[0]?.model?.trainingDataItemsCount) +
													parseInt(currentModel.vertexEvaluation?.evaluations[0]?.model?.validationDataItemsCount) + 
													parseInt(currentModel.vertexEvaluation?.evaluations[0]?.evaluatedCount)
												}}
											</div>
											<div class="text-gray-600 dark:text-gray-600 mt-0.5">{{$t('Images')}}</div>
										</div>

									</div>

									<div class="mx-auto w-10/12 xxl:w-2/3 mt-3" v-if="currentModel.vertexEvaluation?.evaluations[0]">
										
										<!-- Train -->
										<div class="flex items-center mt-2">
											<div class="w-2 h-2 bg-theme-35 rounded-full mr-3" style="background-color: #333333"></div>
											<span class="truncate">{{$t('Train')}}</span>
											<div class="h-px flex-1 border border-r border-dashed border-gray-400 mx-2 mt-3"></div>
											<span class="font-medium xl:ml-auto">{{ currentModel.vertexEvaluation?.evaluations[0]?.model.trainingDataItemsCount }}</span>
										</div>
										
										<!-- Validation -->
										<div class="flex items-center mt-2">
											<div class="w-2 h-2 bg-theme-35 rounded-full mr-3" style="background-color: #1d8102"></div>
											<span class="truncate">{{$t('Validation')}}</span>
											<div class="h-px flex-1 border border-r border-dashed border-gray-400 mx-2 mt-3"></div>
											<span class="font-medium xl:ml-auto">{{ currentModel.vertexEvaluation?.evaluations[0]?.model.validationDataItemsCount }}</span>
										</div>

										<!-- Test -->
										<div class="flex items-center mt-2" v-if="currentModel.vertexEvaluation?.evaluations[0]?.evaluatedCount">
											<div class="w-2 h-2 bg-theme-35 rounded-full mr-3" style="background-color: #1d8102"></div>
											<span class="truncate">{{$t('Test')}}</span>
											<div class="h-px flex-1 border border-r border-dashed border-gray-400 mx-2 mt-3"></div>
											<span class="font-medium xl:ml-auto">{{ currentModel.vertexEvaluation?.evaluations[0]?.evaluatedCount }}</span>
										</div>

									</div>

								</div>

							</div>

							<!-- Model configuration table -->
							<div class="col-span-7 pb-0 text-left mt-5">
								
								<table class="table table--sm text-xs text-left">
									
									<tbody>

										<tr v-if="currentModel.db.automl">
											<td class="border"><span>{{$t('Storage bucket')}}</span></td>
											<td class="border bg-gray-100" v-if="currentModel.db?.savedModel">
												<div 
													class="copyButton cursor-pointer"
													@click="copyToClipboard(currentModel.db.savedModel)"
													:data-clipboard-text="currentModel.db.savedModel"
												>
													<span class="text-green-700">{{$t('model saved')}} <CopyIcon class="w-4 h-4 ml-2 hover:text-blue-900" /></span>
												</div>
											</td>
											<td class="border bg-gray-100" v-else>
												<div v-if="Layout.loading" class="w-10 h-10">
													<LoadingIcon icon="three-dots" class="w-12 h-12" />
												</div>
												<button
													v-else
													type="button"
													@click="exportModelToBucket()"
													class="btn bg-theme-14 text-white my-2 font-normal zoom-in"
												>
													{{ $t('Export to Storage Bucket') }}
												</button>
											</td>
										</tr>

										<tr v-if="currentModel.vertexEvaluation?.evaluations && currentModel.vertexEvaluation?.evaluations[0]?.model?.versionId">
											<td class="border"><span>{{$t('Version')}}</span></td>
											<td class="border bg-gray-100"><span>{{ currentModel.vertexEvaluation.evaluations[0].model.versionId }}</span></td>
										</tr>

										<tr v-if="currentModel.vertexEvaluation?.evaluations && currentModel.vertexEvaluation?.evaluations[0]?.model?.training?.budgetMilliNodeHours">
											<td class="border"><span>{{$t('Budget')}}</span></td>
											<td class="border bg-gray-100">
												<span>{{ currentModel.vertexEvaluation.evaluations[0].model.training.budgetMilliNodeHours }} {{$t('milliNodeHours')}}</span>
											</td>
										</tr>

										<tr v-if="currentModel.vertexEvaluation?.evaluations && currentModel.vertexEvaluation?.evaluations[0]?.model?.training?.costMilliNodeHours">
											<td class="border"><span>{{$t('Cost')}}</span></td>
											<td class="border bg-gray-100">
												<span>{{ currentModel.vertexEvaluation.evaluations[0].model.training.costMilliNodeHours }} {{$t('milliNodeHours')}}</span>
											</td>
										</tr>

										<tr v-if="currentModel.vertexEvaluation?.evaluations && currentModel.vertexEvaluation?.evaluations[0]?.model?.training?.stopReason">
											<td class="border"><span>{{$t('Stop reason')}}</span></td>
											<td class="border bg-gray-100">
												<span :class="currentModel.vertexEvaluation.evaluations[0].model.training.stopReason=='MODEL_CONVERGED' ? 'text-green-500' : 'text-blue-300'">
													{{ currentModel.vertexEvaluation.evaluations[0].model.training.stopReason }}
												</span>
											</td>
										</tr>

										<tr v-if="currentModel?.vertex?.createTime">
											<td class="border"><span>{{$t('Created')}}</span></td>
											<td class="border bg-gray-100">
												<span>{{ $helper.getFbDate(currentModel.vertex.createTime) }}</span>
											</td>
										</tr>

									</tbody>

								</table>

							</div>

							<!-- Evaluation Table -->
							<div class="col-span-12" v-if="currentModel?.vertexEvaluation?.render">
								<div v-html="currentModel.vertexEvaluation.render"></div>
							</div>

						</div>
		
						<!-- AWS Results (Semi-supervised) --> 
						<table v-if="currentModel.db?.aws && !semiSupervisedEvaluationObjects?.length && !currentModel.awsEvaluation?.loading" class="table table--sm text-xs text-left mt-5">
							<tbody>

								<tr v-if="currentModel.db?.aws">
									<td class="border w-52"><span>{{$t('Identifier')}}</span></td>
									<td class="border"><span>{{ currentModel.db.aws }}</span></td>
								</tr>

								<tr v-if="currentModel.db?.aws && (currentModel.db.ModelVersion || currentModel?.awsModel?.response?.ModelDescription?.ModelVersion)">
									<td class="border"><span>{{$t('Version')}}</span></td>
									<td class="border">
										<span v-if="currentModel.db.ModelVersion">{{  currentModel.db.ModelVersion }}</span>
										<span v-if="currentModel?.awsModel?.response?.ModelDescription?.ModelVersion!=currentModel.db.ModelVersion">({{ currentModel?.awsModel?.response?.ModelDescription?.ModelVersion }})</span>
									</td>
								</tr>

								<tr v-if="currentModel?.awsModel && currentModel?.awsModel?.response?.ModelDescription?.Status && currentModel?.awsModel?.response?.ModelDescription?.Status!='TRAINING_FAILED'">
									<td class="border">{{$t('Packages')}}</td>
									<td class="border text-left" v-if="currentModel.packageJobs?.response?.ModelPackagingJobs.length">
										<div v-for="job in currentModel.packageJobs?.response?.ModelPackagingJobs" :key="job" class=" mt-3 mb-3">
											<span class="py-1 px-5 rounded-full text-xs cursor-pointer mr-2" 
											:class="job.Status=='SUCCEEDED'?'bg-green-600 text-white':'bg-gray-300 text-gray-600'"
											@click="loadModelPackagingJob(job.JobName);">{{ job.JobName }}</span> 
										</div>
									</td>
									<td class="border" v-else>
										<div class="text-left mt-1">{{$t('The model does not packaging jobs')}}.</div>
										<button type="button" class="btn bg-gray-600 font-normal text-white mt-2">{{ $t('Create new package') }}</button>
									</td>
								</tr>

								<tr v-if="currentModel?.awsModel?.response?.ModelDescription?.Status">
									<td class="border"><span>{{$t('Status')}}</span></td>
									<td class="border" :class="currentModel?.awsModel?.response?.ModelDescription?.Status=='TRAINING_FAILED' ? 'text-red-500' : ''">
										<span>{{  currentModel?.awsModel?.response?.ModelDescription?.Status }}</span>
										<span v-if="currentModel?.awsModel?.response?.ModelDescription?.StatusMessage" class="pl-3">{{  currentModel?.awsModel?.response?.ModelDescription?.StatusMessage }}</span>
									</td>
								</tr>

								<tr v-if="currentModel?.awsModel?.response?.ModelDescription?.CreationTimestamp">
									<td class="border"><span>{{$t('Created')}}</span></td>
									<td class="border"><span>{{  currentModel?.awsModel?.response?.ModelDescription?.CreationTimestamp }}</span></td>
								</tr>

							</tbody>
						</table>

						<!-- AWS Metrics (Semi-supervised) -->
						<div v-if="currentModel?.db?.aws" class="grid chat grid-cols-12 gap-6 mt-3">
							<div class="col-span-12 p-0">
								<div class="grid chat grid-cols-12 gap-5 py-1">

									<template v-if="currentModel?.awsEvaluation && currentModel?.awsEvaluation?.status != 'error'">

										<div class="col-span-12 text-center">

											<div v-if="currentModel.awsEvaluation?.loading">
												<div class="intro-y col-span-12 lg:col-span-12 flex flex-col justify-center items-center mt-0">
													<div class="flex items-center justify-center mt-5">  
														<img :src="require(`@/assets/images/rosepetal/icon/logoLoadingGray.png`)" class="w-20" />
													</div>
													<LoadingIcon icon="three-dots" class="w-12 h-12" />
												</div>
											</div>

											<div v-if="!semiSupervisedEvaluationObjects?.length && !currentModel.awsEvaluation?.loading">
												<div class="grid chat grid-cols-12 gap-0 py-2 px-2 text-gray-600">
													<div class="col-span-12 text-gray-600 text-center mb-2">
														<h2 class="text-xl truncate">{{ $t('Model performance metrics') }}</h2>
														<div class="text-gray-600 text-xs font-normal text-center mt-2">{{$t('Use model performance metrics to find the strengths and weaknesses of the model')}}.</div>
													</div>
													<div class="col-span-4 text-center mx-auto mt-5">
														<div class="relative">
															<div class="w-72">
																<ReportDonutChart
																	:height="190"
																	:type="'doughnut'"
																	:options="{
																		responsive: true,
																		maintainAspectRatio: false,
																		cutout: '70%',
																		plugins: {
																			legend: { display: false }
																		}
																	}"
																	:data="{
																	labels: ['Precision'],
																	datasets: [{
																		data: [
																		(
																			(currentModel.awsEvaluation.manifestResume?.anomaly?.truePositive || 0) /
																			(currentModel.awsEvaluation.manifestResume?.anomaly?.total || 1)
																		* 100
																		).toFixed(0)
																		],
																		backgroundColor: ['#1d8102']
																	}]
																	}"
																/>
															</div>

															<div class="flex flex-col justify-center items-center absolute w-full h-full top-0 left-0">
																<div class="text-xl xxl:text-2xl font-medium">{{ ((currentModel.awsEvaluation.manifestResume?.anomaly?.truePositive/currentModel.awsEvaluation.manifestResume?.anomaly?.total)*100).toFixed(0)  }}%</div>
																<div class="text-gray-600 dark:text-gray-600 mt-0.5">{{$t('Precision')}}</div>
															</div>
														</div>
														<div class="mx-auto w-f xxl:w-3/4 mt-5">
															<div class="flex items-center">
																<span class="mr-2 font-bold">{{ currentModel.awsEvaluation.manifestResume?.anomaly?.truePositive }}</span>
																<span class="truncate">{{$t('anomalies were correct of')}}</span>
															</div>
															<div class="flex items-center mt-1">
																<span class="mr-2 font-bold">{{ currentModel.awsEvaluation.manifestResume?.anomaly?.total }}</span>
																<span class="truncate">{{$t('total predictions')}}</span>
															</div>
														</div>
													</div>
													<div class="col-span-4 text-center mx-auto mt-5">
														<div class="relative">
															<div class="w-72">
																<ReportDonutChart
																	:height="190"
																	:type="'doughnut'"
																	:options="{
																		responsive: true,
																		maintainAspectRatio: false,
																		cutout: '70%',
																		plugins: {
																		legend: { display: false }
																		}
																	}"
																	:data="{
																		labels: ['Precision'],
																		datasets: [{
																		data: [
																			(
																			(currentModel.awsEvaluation.manifestResume?.anomaly?.truePositive || 0) /
																			(currentModel.awsEvaluation.manifestResume?.anomaly?.total || 1)
																			* 100
																			).toFixed(0)
																		],
																		backgroundColor: ['#1d8102']
																		}]
																	}"
																/>
															</div>
															<div class="flex flex-col justify-center items-center absolute w-full h-full top-0 left-0">
																<div class="text-xl xxl:text-2xl font-medium">
																	{{ 
																	((((currentModel.awsEvaluation.manifestResume?.anomaly?.predictions+currentModel.awsEvaluation.manifestResume?.normal?.predictions)-(currentModel.awsEvaluation.manifestResume?.anomaly?.falsePositive+currentModel.awsEvaluation.manifestResume?.normal?.falseNegative))/currentModel.awsEvaluation.manifestResume?.totalTestImages)*100).toFixed(0) 
																	}}%</div>
																<div class="text-gray-600 dark:text-gray-600 mt-0.5">{{$t('F1 Score')}}</div>
															</div>
														</div>
														<div class="mx-auto w-full mt-5 text-center">
															<span class="truncate">{{$t('The overall performance')}}</span>
														</div>
													</div>
													<div class="col-span-4 text-center mx-auto mt-5">
														<div class="relative">
															<div class="w-72">
																<ReportDonutChart
																	:height="190"
																	:type="'doughnut'"
																	:options="{
																		responsive: true,
																		maintainAspectRatio: false,
																		cutout: '70%',
																		plugins: {
																		legend: { display: false }
																		}
																	}"
																	:data="{
																		labels: ['Precision'],
																		datasets: [{
																		data: [
																			(
																			(currentModel.awsEvaluation.manifestResume?.anomaly?.truePositive || 0) /
																			(currentModel.awsEvaluation.manifestResume?.anomaly?.total || 1)
																			* 100
																			).toFixed(0)
																		],
																		backgroundColor: ['#1d8102']
																		}]
																	}"
																/>
															</div>
															<div class="flex flex-col justify-center items-center absolute w-full h-full top-0 left-0">
																<div class="text-xl xxl:text-2xl font-medium">{{ ((currentModel.awsEvaluation.manifestResume?.anomaly?.truePositive/currentModel.awsEvaluation.manifestResume?.anomaly?.predictions)*100).toFixed(0)  }}%</div>
																<div class="text-gray-600 dark:text-gray-600 mt-0.5">{{$t('Recovery')}}</div>
															</div>
														</div>
														<div class="mx-auto w-f xxl:w-4/4 mt-5">
															<div class="overflow-hidden">
																<div class="flex items-center">
																	<span class="truncate pl-5">{{$t('were correct')}}</span>
																	<span class="mr-2 ml-2 font-bold">{{ currentModel.awsEvaluation.manifestResume?.anomaly?.truePositive }}</span>
																	<span class="truncate text-xs">{{$t('anomalies predicted of')}}</span>
																</div>
																<div class="flex items-center mt-1">
																	<span class="mr-2 font-bold pl-5">{{ currentModel.awsEvaluation.manifestResume?.anomaly?.predictions }}</span>
																	<span class="truncate">{{$t('total anomalies')}}</span>
																</div>
															</div>
														</div>
													</div>
													<div class="col-span-12 mt-5 text-gray-600 text-center">
														<h2 class="text-xl truncate">{{ $t('About test results') }}</h2>
													</div>
													<div class="col-span-6 text-left mt-5 p-5 border ml-2 rounded mr-1">
														<h2 class="text-base font-medium truncate">{{ $t('Predicted as normal') }}</h2>
														<div class="grid chat grid-cols-12 gap-0 py-1 text-gray-600">
															<div class="col-span-6">
																<div class="h-10 font-medium">{{ $t('Correct') }} <span class="text-xs font-normal">({{ $t('true negative') }})</span></div>
																<h2 class="text-5xl font-medium truncate px-5">{{ currentModel.awsEvaluation.manifestResume?.normal?.trueNegative }}</h2>
															</div>
															<div class="col-span-6">
																<div class="h-10 font-medium">{{ $t('Incorrect') }} <span class="text-xs font-normal">({{ $t('false negative') }})</span></div>
																<h2 class="text-5xl font-medium truncate px-5">{{ currentModel.awsEvaluation.manifestResume?.normal?.falseNegative }}</h2>
															</div>
														</div>   
													</div>
													<div class="col-span-6 text-left mt-5 p-5 border mr-2 rounded" style="border-left: none;">
														<h2 class="text-base font-medium truncate">{{ $t('Predicted as anomaly') }}</h2>
														<div class="grid chat grid-cols-12 gap-0 py-1 text-gray-600">
															<div class="col-span-6">
																<div class="h-10 font-medium">{{ $t('Correct') }} <span class="text-xs font-normal">({{ $t('true positive') }})</span></div>
																<h2 class="text-5xl font-medium truncate px-5">{{ currentModel.awsEvaluation.manifestResume?.anomaly?.truePositive }}</h2>
															</div>
															<div class="col-span-6">
																<div class="h-10 font-medium">{{ $t('Incorrect') }} <span class="text-xs font-normal">({{ $t('false positive') }})</span></div>
																<h2 class="text-5xl font-medium truncate px-5">{{ currentModel.awsEvaluation.manifestResume?.anomaly?.falsePositive }}</h2>
															</div>
														</div>   
													</div>
												</div>
											</div>

											<div v-else class="grid chat grid-cols-12 gap-2 text-gray-600 mt-0">
												
												<div class="col-span-12" v-if="currentModel?.awsEvaluation?.order && !currentModel.awsEvaluation?.loading">
													<select class="form-select bg-white cursor-pointer w-44 mt-0" v-model="currentModel.awsEvaluation.filterPrediction" @change="loadEvaluationObjects(currentModel?.awsEvaluation?.manifest)">
														<option value="all">{{$t('All prediction')}}</option>
														<option value="correct">{{$t('Correct')}}</option>
														<option value="incorrect">{{$t('Incorrect')}}</option>
													</select>
													<select class="form-select bg-white cursor-pointer w-60 mt-0 ml-2" v-model="currentModel.awsEvaluation.order" @change="loadEvaluationObjects(currentModel?.awsEvaluation?.manifest)">
														<option value="asc">{{$t('Lowest confidence')}}</option>
														<option value="desc">{{$t('Highest confidence')}}</option>
													</select>
													<div style="display: inline-block" class="w-44 ml-5">
														<Slider v-model="currentModel.awsEvaluation.sizeObject" :min="25" :max="75" :step="25" :merge="25" :tooltips="false" :lazy="true" /></div>
													<div v-if="!semiSupervisedEvaluationObjects[0]?.empty" class="text-xs text-center pt-3 pb-2">
														- {{$t('Showing')}} {{ semiSupervisedEvaluationObjects?.length }} {{$t('of')}} {{ currentModel.awsEvaluation.manifestResume?.totalTestImages }} {{$t('test images')}} - 
													</div>
												</div>
												
												<div v-if="semiSupervisedEvaluationObjects[0]?.empty" class="col-span-12 py-20">
													- {{$t('No results')}} -
												</div>

												<div v-else class="col-span-12 text-gray-600 text-center">
													<div v-for="(t, indx) in semiSupervisedEvaluationObjects" :key="indx" class="relative inline-block rounded border border-gray-200 mb-2"
														:style="currentModel.awsEvaluation.sizeObject ? 'float: left !important; width: ' + ((currentModel.awsEvaluation.sizeObject == 25 ? 33 : currentModel.awsEvaluation.sizeObject == 75 ? 100 : currentModel.awsEvaluation.sizeObject)-0.5)+'% !important; margin-right: 0.5% !important' : ''">
														
														<div class="pt-3 px-2 pb-10 rounded border" style="background-color: #fff">
															
															<!-- Name -->
															<div class="text-lg mb-3 truncate">
																{{ semiSupervisedEvaluationObjects[indx]?.obj['source-ref'].toString().split("/").pop() }}
															</div>
															
															<!-- Image -->
															<img class="w-full rounded" :src="semiSupervisedEvaluationObjects[indx]?.imageSrc?.renderSrc" />
															
															<!-- Metadata -->
															<div v-if="currentModel?.awsEvaluation?.manifestResume">
																<div class="pt-3 text-left font-medium" :class="currentModel.awsEvaluation.sizeObject!=25 ? 'px-2' : ''">
																	
																	<span v-if="semiSupervisedEvaluationObjects[indx]?.obj['anomaly-label'] == semiSupervisedEvaluationObjects[indx]?.obj['anomaly-label-detected']">
																		<img :src="require(`@/assets/images/rosepetal/icon/tick.png`)" class="w-5" style="display: inline" /> <span class="text-green-700 align-middle font-normal">{{$t('Correct')}}</span> 
																	</span>

																	<span v-else>
																		<X-circleIcon  class="text-red-600 w-5 h-5"/> 
																		<span class="text-red-700">{{$t('Incorrect')}}</span>
																	</span>
																	
																	<span class="float-right ml-aut text-gray-600 text-center">
																		<span class="text-xs text-gray-500">{{$t('Confidence')}}</span><br/>
																		<span class="text-lg">{{ (semiSupervisedEvaluationObjects[indx].obj['anomaly-label-detected-metadata']['confidence']*100).toFixed(1) }}%</span>
																	</span>
																	
																	<span class="float-right ml-aut text-gray-600 text-center mr-8">
																		<span class="text-xs text-gray-500">{{$t('Prediction')}}</span><br />
																		{{ semiSupervisedEvaluationObjects[indx]?.obj['anomaly-label-detected-metadata']['class-name'] }}
																	</span>

																</div>
															</div>

														</div>

													</div>
												</div> 

											</div>

										</div>

										<div class="col-span-12 text-center text-lg">{{$t('Training data')}}</div>

										<div class="col-span-5 text-center mr-5">

											<div class="relative">
												<ReportDonutChart
													:height="190"
													:type="'doughnut'"
													:options="{
														responsive: true,
														maintainAspectRatio: false,
														cutout: '70%',
														plugins: {
															legend: { display: false }
														}
													}"
													:data="{
														labels: ['Normal', 'Anomaly'],
														datasets: [{
														data: [
															currentModel?.response?.dataset?.test?.DatasetDescription?.ImageStats?.Normal || 0,
															currentModel?.response?.dataset?.test?.DatasetDescription?.ImageStats?.Anomaly || 0
														],
														backgroundColor: ['#1d8102', '#d13212']
														}]
													}"
												/>
												<div class="flex flex-col justify-center items-center absolute w-full h-full top-0 left-0">
													<div class="text-xl xxl:text-2xl font-medium">{{ $helper.formatScore(currentModel?.response.dataset.train.DatasetDescription.ImageStats["Total"]+currentModel.response.dataset.test.DatasetDescription.ImageStats["Total"]) }}</div>
													<div class="text-gray-600 dark:text-gray-600 mt-0.5">{{$t('Images')}}</div>
												</div>
											</div>

											<div class="mx-auto w-10/12 xxl:w-2/3 mt-8">
												<div class="flex items-center">
													<div class="w-2 h-2 bg-theme-26 rounded-full mr-3" style="background-color: #1d8102"></div>
													<span class="truncate">{{$t('Normal')}}</span>
													<div class="h-px flex-1 border border-r border-dashed border-gray-400 mx-2 mt-3"></div>
													<span class="font-medium xl:ml-auto">
														{{ ((currentModel.response.dataset.test.DatasetDescription.ImageStats["Normal"]/currentModel.response.dataset.test.DatasetDescription.ImageStats["Total"])*100).toFixed(2)  }}%
													</span>
												</div>
												<div class="flex items-center mt-2">
													<div class="w-2 h-2 bg-theme-35 rounded-full mr-3" style="background-color: #d13212"></div>
													<span class="truncate">{{$t('Anomaly')}}</span>
													<div class="h-px flex-1 border border-r border-dashed border-gray-400 mx-2 mt-3"></div>
													<span class="font-medium xl:ml-auto">
														{{ ((currentModel.response.dataset.test.DatasetDescription.ImageStats["Anomaly"]/currentModel.response.dataset.test.DatasetDescription.ImageStats["Total"])*100).toFixed(2)  }}%
													</span>
												</div>
											</div>

										</div>

										<div class="col-span-7 pb-0 text-left">

											<table class="table table--sm text-xs text-center">
												<tbody>
													<tr>
														<td></td>
														<td class="border bg-gray-100"><span>{{$t('TEST')}}</span></td>
														<td class="border bg-gray-100"><span>{{$t('TRAIN')}}</span></td>
													</tr>
													<tr v-for="(t,indx) in currentModel.response.dataset.test.DatasetDescription.ImageStats" :key="indx">
														<td class="border p-2 bg-gray-100">{{ indx }}</td>
														<td class="border p-2"><span>{{ t }}</span></td>
														<td class="border p-2" v-if="currentModel.response.dataset.train.DatasetDescription.ImageStats[indx]"><span>{{ currentModel.response.dataset.train.DatasetDescription.ImageStats[indx] }}</span></td>
													</tr>
												</tbody>
											</table>

											<div v-if="currentModel.db && currentModel.db.tagMap" class="pt-5">
												<div class="text-sm text-gray-600">{{$t('Label Map')}}</div>
												<div class="grid chat grid-cols-12 gap-1 py-2  text-gray-600">
													<div class="col-span-6 text-left">
														<table class="table table--sm text-xs">
															<tbody>
																<tr><td class="border text-center text-green-700"><span>{{$t('NORMAL')}}</span></td></tr>
																<tr v-for="(t,indx) in JSON.parse(currentModel.db.tagMap)['normal']" :key="indx">
																	<td class="border bg-gray-100 text-center"><span>{{ t }}</span></td>
																</tr>
															</tbody>
														</table>
													</div>
													<div class="col-span-6 text-left">
														<table class="table table--sm text-xs">
															<tbody>
																<tr><td class="border text-center text-red-700"><span>{{$t('ANOMALY')}}</span></td></tr>
																<tr v-for="(t,indx) in JSON.parse(currentModel.db.tagMap)['anomaly']" :key="indx">
																	<td class="border bg-gray-100 text-center"><span>{{ t }}</span></td>
																</tr>
															</tbody>
														</table>
													</div>
												</div> 
											</div>

										</div>

									</template>

									<div v-else-if="currentModel?.awsEvaluation?.status == 'error'" class="col-span-12 text-left text-xs">
										<div class="text-center text-red-500">{{ currentModel.awsEvaluation.error }}</div>
									</div>

								</div>
							</div>
						</div>

						<!-- Vision Platform Results -->
						<div v-if="currentModel?.db?.evaluation?.service === 'visionPlatform'" class="grid chat grid-cols-12 gap-6">

							<!-- Evaluation Diagram Section -->
							<div class="col-span-5 text-center mt-8" v-if="currentModel.db.evaluation?.status === 'finished'">
								
								<!-- Donut Diagram -->
								<div class="relative" v-if="currentModel.db.evaluation?.imageDataDivision">
									
									<!-- Graphic -->
									<ReportDonutChart
										:height="190"
										:type="'doughnut'"
										:options="{
											responsive: true,
											maintainAspectRatio: false,
											cutout: '70%',
											plugins: {
												legend: { display: false }
											}
										}"
										:data="{
											labels: ['Train', 'Test', 'Validation'],
											datasets: [{
												data: [
													currentModel.db.evaluation.imageDataDivision.train,
													currentModel.db.evaluation.imageDataDivision.test,
													currentModel.db.evaluation.imageDataDivision.validation
												],
												backgroundColor: ['#333333', '#1d8102', '#09491b']
											}]
										}"
									/>

									<!-- Total images value -->
									<div class="flex flex-col justify-center items-center absolute w-full h-full top-0 left-0">
										<div class="text-xl xxl:text-2xl font-medium">
											{{ 
												parseInt(currentModel.db.evaluation.imageDataDivision.train) +
												parseInt(currentModel.db.evaluation.imageDataDivision.test) + 
												parseInt(currentModel.db.evaluation.imageDataDivision.validation)
											}}
										</div>
										<div class="text-gray-600 dark:text-gray-600 mt-0.5">{{$t('Images')}}</div>
									</div>

								</div>

								<!-- Donut Diagram values-->
								<div class="mx-auto w-10/12 xxl:w-2/3 mt-3" v-if="currentModel.db.evaluation?.imageDataDivision">
									
									<!-- Train value -->
									<div class="flex items-center mt-2">
										<div class="w-2 h-2 bg-theme-35 rounded-full mr-3" style="background-color: #333333"></div>
										<span class="truncate">{{$t('Train')}}</span>
										<div class="h-px flex-1 border border-r border-dashed border-gray-400 mx-2 mt-3"></div>
										<span class="font-medium xl:ml-auto">{{ currentModel.db.evaluation.imageDataDivision.train }}</span>
									</div>

									<!-- Test value -->
									<div class="flex items-center mt-2">
										<div class="w-2 h-2 bg-theme-35 rounded-full mr-3" style="background-color: #1d8102"></div>
										<span class="truncate">{{$t('Test')}}</span>
										<div class="h-px flex-1 border border-r border-dashed border-gray-400 mx-2 mt-3"></div>
										<span class="font-medium xl:ml-auto">{{ currentModel.db.evaluation.imageDataDivision.test }}</span>
									</div>

									<!-- Validation value -->
									<div class="flex items-center mt-2">
										<div class="w-2 h-2 bg-theme-35 rounded-full mr-3" style="background-color: #1d8102"></div>
										<span class="truncate">{{$t('Validation')}}</span>
										<div class="h-px flex-1 border border-r border-dashed border-gray-400 mx-2 mt-3"></div>
										<span class="font-medium xl:ml-auto">{{ currentModel.db.evaluation.imageDataDivision.validation }}</span>
									</div>

								</div>

							</div>

							<!-- Model Configuration Table -->
							<div class="col-span-7 pb-0 text-left mt-5">
								
								<table class="table table--sm text-xs text-left">
									
									<tbody>

										<tr v-if="currentModel.db.evaluation?.configuration?.budget">
											<td class="border"><span>{{$t('Budget')}}</span></td>
											<td class="border bg-gray-100">
												<span>{{ currentModel.db.evaluation.configuration.budget }} {{$t('milliNodeHours')}}</span>
											</td>
										</tr>

										<tr v-if="currentModel.db.evaluation?.configuration?.cost">
											<td class="border"><span>{{$t('Cost')}}</span></td>
											<td class="border bg-gray-100">
												<span>{{ currentModel.db.evaluation.configuration.cost }} {{$t('milliNodeHours')}}</span>
											</td>
										</tr>

										<tr v-if="currentModel.db.evaluation?.configuration?.stopReason">
											<td class="border"><span>{{$t('Stop reason')}}</span></td>
											<td class="border bg-gray-100">
												<span :class="currentModel.db.evaluation.configuration.stopReason =='MODEL_CONVERGED' ? 'text-green-500' : 'text-blue-300'">
													{{ currentModel.db.evaluation.configuration.stopReason }}
												</span>
											</td>
										</tr>

										<tr v-if="currentModel.db.evaluation?.configuration?.createdAt">
											<td class="border"><span>{{$t('Created')}}</span></td>
											<td class="border bg-gray-100">
												<span>{{ currentModel.db.evaluation.configuration.createdAt }}</span>
											</td>
										</tr>

									</tbody>

								</table>

							</div>

							<!-- Model Performance Metrics -->
							<template v-if="!Layout.showAnnotations">

								<!-- Model Performance Metrics -->
								<div class="col-span-12 text-left" v-if="currentModel?.db?.evaluation?.metrics && currentModel?.db?.evaluation?.status == 'finished'">
									<div class="text-center">

										<div v-if="Layout.loading">
											<div class="intro-y col-span-12 lg:col-span-12 flex flex-col justify-center items-center mt-0">
												<div class="flex items-center justify-center mt-5">  
													<img :src="require(`@/assets/images/rosepetal/icon/logoLoadingGray.png`)" class="w-20" />
												</div>
												<LoadingIcon icon="three-dots" class="w-12 h-12" />
											</div>
										</div>

										<div v-else>

											<div class="grid chat grid-cols-12 gap-0 py-2 px-2 text-gray-600">

												<!-- Title -->
												<div class="col-span-12 text-gray-600 text-center mb-2">
													<h2 class="text-xl truncate">{{ $t('Model performance metrics') }}</h2>
													<div class="text-gray-600 text-xs font-normal text-center mt-2">{{$t('Use model performance metrics to find the strengths and weaknesses of the model')}}.</div>
												</div>

												<!-- Precision -->
												<div class="col-span-4 text-center mx-auto mt-5">

													<div class="relative">
														<div class="w-72">
															<ReportDonutChart
																:height="190"
																:type="'doughnut'"
																:options="{
																	responsive: true,
																	maintainAspectRatio: false,
																	cutout: '70%',
																	plugins: {
																	legend: { display: false }
																	}
																}"
																:data="{
																	labels: ['Precision'],
																	datasets: [{
																	data: [
																		(currentModel.db.evaluation.metrics.precision * 100).toFixed(0)
																	],
																	backgroundColor: ['#1d8102']
																	}]
																}"
															/>
														</div>
														<div class="flex flex-col justify-center items-center absolute w-full h-full top-0 left-0">
															<div class="text-xl xxl:text-2xl font-medium">{{ (currentModel.db.evaluation.metrics.precision * 100).toFixed(0) }}%</div>
															<div class="text-gray-600 dark:text-gray-600 mt-0.5">{{ $t('Precision') }}</div>
														</div>
													</div>

													<!-- <div class="mx-auto w-f xxl:w-3/4 mt-5">
														<div class="flex items-center">
															<span class="mr-2 font-bold">{{ currentModel.db.evaluation.manifestResume?.anomaly?.truePositive }}</span>
															<span class="truncate">{{$t('anomalies were correct of')}}</span>
														</div>
														<div class="flex items-center mt-1">
															<span class="mr-2 font-bold">{{ currentModel.db.evaluation.manifestResume?.anomaly?.total }}</span>
															<span class="truncate">{{$t('total predictions')}}</span>
														</div>
													</div> -->

												</div>
												
												<!-- F1 Score -->
												<div class="col-span-4 text-center mx-auto mt-5">

													<div class="relative">
														<div class="w-72">
															<ReportDonutChart
																:height="190"
																:type="'doughnut'"
																:options="{
																	responsive: true,
																	maintainAspectRatio: false,
																	cutout: '70%',
																	plugins: {
																	legend: { display: false }
																	}
																}"
																:data="{
																	labels: ['F1 Score'],
																	datasets: [{
																	data: [
																		(currentModel.db.evaluation.metrics.f1 * 100).toFixed(0)
																	],
																	backgroundColor: ['#1d8102']
																	}]
																}"
															/>
														</div>
														<div class="flex flex-col justify-center items-center absolute w-full h-full top-0 left-0">
															<div class="text-xl xxl:text-2xl font-medium">{{ (currentModel.db.evaluation.metrics.f1 * 100).toFixed(0) }}%</div>
															<div class="text-gray-600 dark:text-gray-600 mt-0.5">{{$t('F1 Score')}}</div>
														</div>
													</div>

													<div class="mx-auto w-full mt-5 text-center">
														<span class="truncate">{{$t('Overall performance')}}</span>
													</div>

												</div>
												
												<!-- Recall -->
												<div class="col-span-4 text-center mx-auto mt-5">
													
													<div class="relative">
														<div class="w-72">
															<ReportDonutChart
																:height="190"
																:type="'doughnut'"
																:options="{
																	responsive: true,
																	maintainAspectRatio: false,
																	cutout: '70%',
																	plugins: {
																	legend: { display: false }
																	}
																}"
																:data="{
																	labels: ['Recall'],
																	datasets: [{
																	data: [
																		(currentModel.db.evaluation.metrics.recall * 100).toFixed(0)
																	],
																	backgroundColor: ['#1d8102']
																	}]
																}"
															/>
														</div>
														<div class="flex flex-col justify-center items-center absolute w-full h-full top-0 left-0">
															<div class="text-xl xxl:text-2xl font-medium">{{ (currentModel.db.evaluation.metrics.recall * 100).toFixed(0) }}%</div>
															<div class="text-gray-600 dark:text-gray-600 mt-0.5">{{$t('Recall')}}</div>
														</div>
													</div>

													<!-- <div class="mx-auto w-f xxl:w-4/4 mt-5">
														<div class="overflow-hidden">
															<div class="flex items-center">
																<span class="truncate pl-5">{{$t('were correct')}}</span>
																<span class="mr-2 ml-2 font-bold">{{ currentModel.db.evaluation.manifestResume?.anomaly?.truePositive }}</span>
																<span class="truncate text-xs">{{$t('anomalies predicted of')}}</span>
															</div>
															<div class="flex items-center mt-1">
																<span class="mr-2 font-bold pl-5">{{ currentModel.db.evaluation.manifestResume?.anomaly?.predictions }}</span>
																<span class="truncate">{{$t('total anomalies')}}</span>
															</div>
														</div>
													</div> -->

												</div>

											</div>
										</div>

									</div>
								</div>

								<!-- Evaluation Graphics (Tags, Confusion Matrix) -->
								<div class="col-span-12 flex overflow-x-auto" v-if="currentModel?.db?.evaluation?.render">
									<div v-html="currentModel.db.evaluation.render"></div>
								</div>

							</template>

							<!-- Model Annotations Images -->
							<template v-else>

								<div v-if="Layout.annotationsImages && Object.keys(Layout.annotationsImages).length" class="col-span-12 scroll-container w-full px-2">
									
									<div class="images-wrapper">

										<div class="mb-4 px-2">

											<label class="mr-2 font-semibold text-sm">{{$t('Filter by class')}}:</label>

											<select v-model="selectedClass" class="border rounded px-2 py-1 text-sm">
												<option value="all">{{$t('All')}}</option>
												<option v-for="cls in availableClasses" :key="cls" :value="cls">
													{{ cls }}
												</option>
											</select>

										</div>

										<div v-for="(annotation, index) in filteredAnnotations" :key="index" class="image-item p-3 rounded border">
											
											<div class="text-center text-lg mb-3 truncate">
												{{ annotation.name }}
											</div>

											<img 
												:src="annotation.cachedUrl" 
												width="300" 
												height="auto" 
												class="mx-auto cursor-pointer hover:opacity-80 transition"
												@click="openModal(index)"
											/>

											<div class="pt-3 font-medium px-2 flex justify-between items-right">
												
												<span class="text-center text-gray-600">
													<span class="text-xs text-gray-500">{{$t('Prediction')}}</span><br />
													{{ annotation.class }}
												</span>

												<span class="text-center text-gray-600">
													<span class="text-xs text-gray-500">{{$t('Confidence')}}</span><br/>
													<span class="text-lg">{{ (annotation.confidence * 100).toFixed(1) }}%</span>
												</span>

											</div>

										</div>
										
									</div>

									<!-- Annotation Canvas -->
									<div v-if="showModal" class="fixed inset-0 z-50 bg-black bg-opacity-70 flex items-center justify-center" @click.self="closeModal">
										
										<div class="relative bg-white rounded shadow-lg p-4 max-w-4xl w-full flex flex-col items-center" style="height: 80vh; background-color: #f2f3f3;">

											<button @click.stop="closeModal"
												class="absolute top-4 right-4 bg-white p-2 shadow hover:bg-gray-100 disabled:opacity-50">
												<XIcon class="w-5 h-5" />
											</button>

											<div class="text-center text-lg mb-4 font-semibold">
												{{ currentAnnotation?.name }}
											</div>

											<div class="flex flex-1 items-center w-full overflow-hidden">
												
												<button @click.stop="prevImage" class="bg-white p-2 shadow hover:bg-gray-100 disabled:opacity-50"
													:disabled="selectedIndex === 0">
													<ArrowLeftIcon class="w-5 h-5" />
												</button>

												<div ref="canvasWrapper" class="h-full w-full justify-center items-center">
													<canvas ref="fabricCanvas" class="w-full h-full"></canvas>
												</div>

												<button @click.stop="nextImage" class="bg-white p-2 shadow hover:bg-gray-100 disabled:opacity-50"
													:disabled="selectedIndex === annotationsList.length - 1">
													<ArrowRightIcon class="w-5 h-5" />
												</button>

											</div>

											<div class="pt-4 text-center">
												<div>
													<strong>{{$t('Prediction')}}:</strong> {{ currentAnnotation?.class }}
												</div>
												<div>
													<strong>{{$t('Confidence')}}:</strong> {{ (currentAnnotation?.confidence * 100).toFixed(1) }}%
												</div>
											</div>

										</div>

									</div>

								</div>

							</template>

						</div>

					</div>

				</div>
			</div>

		</div>

		<div v-else class="grid chat grid-cols-12">
			<div class="col-span-12 flex flex-col justify-center items-center pb-10">
				<div class="flex items-center justify-center mt-10"><img :src="require(`@/assets/images/rosepetal/icon/logoLoadingGray.png`)" class="w-20" /></div>
				<LoadingIcon icon="three-dots" class="w-8 h-8" />
				<p class="text-sm">{{ $t('Loading models...') }}</p>
			</div>
		</div>

		<!-- Delete Model --> 
		<div id="model-delete-box"  class="modal" tabindex="-1" aria-hidden="true" data-keyboard="false" data-backdrop="static">
			<div class="modal-dialog modal-md">
				<div class="modal-content">
					<label class="form-label float-right ml-auto :hover:text-blue-500 cursor-pointer" data-dismiss="modal"><XIcon class="w-8 h-8" /></label>
					<div class="modal-body p-5 text-center">
						<div class="p-5 px-2 text-center" v-if="currentModel?.db">
							<div class="text-base mt-5">{{ $t('Are you sure to delete this model') }}?</div>
							<div class="text-red-700 text-base mt-1">{{currentModel.db.id}}</div>  
							<div class="text-gray-600 mt-1">{{ $t('This action is irreversible') }}.</div>
						</div>
						<div class="px-5 pb-3 text-center">
							<button id="dismiss-modal-delete" type="button" data-dismiss="modal" class="btn btn-outline-secondary w-24 mr-1 h-10 zoom-in">{{ $t('Cancel') }}</button>
							<button type="button" class="btn rp-bg-blue-1 text-white w-24 zoom-in" @click="deleteModel(); refreshBtn()">{{ $t('Delete') }}</button>
						</div>
					</div>
				</div>
			</div>
		</div>

		<VisionPlatformModal v-if="showVisionPlatform" 
			:dataset="Dataset"
			:dimensions="advancedSettings"
			:trainingMode="Layout.selectedTrainingMode"
			@modelCreated="manageTraining($event)"
			@closeModal="showVisionPlatform = false"
			@switchToValidation="showVisionPlatform = false; $emit('switchToValidation')" />

		<TrainModelModal v-if="showTrainingModal" 
			:model="currentModel.db"
			@trainFinished="showTrainingModal = false; reloadDataset(); loadModel($event)"
			@closeModal="closeTrainingModal"/>



	</div>
</template>

<script setup>
import { onMounted, ref, computed, watch, nextTick  } from "vue";
import { useRouter }		from "vue-router";
import { fabric }			from 'fabric';
import Slider 				from "@vueform/slider";
import cash 				from "cash-dom";
import ClipboardJS			from "clipboard";

import ReportDonutChart		from "@/components/visuals/report-donut-chart/Main.vue";
import VisionPlatformModal 	from "@/modals/model/VisionPlatform.vue";
import TrainModelModal 		from "@/modals/model/TrainModel.vue";
import AdvancedSettings		from "@/modals/model/AdvancedSettings.vue";


import * as rosepetalModel	from "rosepetal-model";
import config 				from '@/etc/rosepetal.json';

const $emit = defineEmits(['updateDataset', 'switchToValidation']);

const router = useRouter();

const $helper 	= rosepetalModel.default.helper;
const $dataset	= rosepetalModel.default.dataset;
const $image	= rosepetalModel.default.image;
const $model	= rosepetalModel.default.model;
const $aws		= rosepetalModel.default.aws;
const $vision	= rosepetalModel.default.vision;
const $firebase = rosepetalModel.default._firebase;

const autoDivisionAlert = computed(() => { 
	if (vpTrainConfig.value) return vpTrainConfig.value.test + vpTrainConfig.value.train + vpTrainConfig.value.validation != 100 ? true : false;
	return false;
});

const enableCreation = computed(() => {
	if (Dataset.value?.validateToTrain?.validated && !autoDivisionAlert.value && !localModelsNames.value.includes(vpTrainConfig.value.modelName) && !Layout.value.loading) return true;
	else return false;
});

const duplicatedName = computed(() => {
	return localModelsNames.value.includes(vpTrainConfig.value.modelName);
});

const annotationsList = computed(() => {
	return Object.values(Layout.value.annotationsImages);
});

const filteredAnnotations = computed(() => {
	if (selectedClass.value === 'all') {
		return annotationsList.value;
	}
	return annotationsList.value.filter(ann => ann.class === selectedClass.value);
});

const currentAnnotation = computed(() => {
	return annotationsList.value[selectedIndex.value];
});

const availableClasses = computed(() => {
	const classes = new Set(annotationsList.value.map(ann => ann.class));
	return Array.from(classes);
});

const modelIsTraining = computed(() => {
	if (currentModel.value) {
		return (!currentModel.value?.db?.automl && currentModel.value?.db?.trainOperation?.operationId) ||
			(!currentModel.value?.db?.trainOperation?.operationId && currentModel.value?.awsModel?.response?.ModelDescription?.Status === 'TRAINING') ||
			(currentModel.value?.db?.service === 'visionPlatform' && (currentModel.value?.db?.status === 'training' || currentModel.value?.db?.status === 'pending'));
	}
	return false;
});

const isLabelingDisabled = computed(
  () => Dataset.value?.data?.type !== 'imageObjectDetection'
);


const props = defineProps({
	dataset: {
		type: Object,
		required: true
	}
});

const Layout = ref({ 
	loading:        		true, 
	loadingAnnotation:		false,
	modalImport:			false,
	showAdvancedSettings:	false,
	annotationsImages:		false,
	annotationsLoaded:		false,
	showAnnotations:		false,
	showTrainingMode:		false,
	selectedTrainingMode: 	null
});

const Dataset = ref({ 
	data:           {}, 
	tags:           {}, 
	localModels:    [], 
	selectedModel:  false 
});

const advancedSettings = ref({
	width:			0,
	height:			0,
	time: 			5,
	batchSize:		16,
	patience: 		0,
	earlyStopping:	false,
	modelType:		"",
	taskType:		"",
	taskModel:		"",
	taskConfig: 	{}
});


const showModal 		= ref(false);
const selectedIndex 	= ref(0);
const selectedClass 	= ref('all');

const clipboard         = ref({});
const currentDataset    = ref({});
const currentModel      = ref({});

const localModelsMenu   = ref([]);
const localModelsNames  = ref([]);

const vpTagMap          = ref({});
const vpTrainConfig 	= ref({  
	method: 				"auto",
	modelName: 				'model_undefined',
	train: 					80, 
	test: 					10,
	validation: 			10,
	setup: 					true, 
	modelTrainBudget: 		1000, 
	modelTrainBudgetMin: 	1000,
	modelTrainBudgetObjMin: 1000,
	modelTrainBudgetStep: 	1000,
	modelTrainBudgetMax: 	24000, 
	modelTrainBudgetObjMax:	30000,
});

const semiSupervisedEvaluationObjects	= ref([]);

const modal 		= (m) => { cash("#" + m).modal("show"); cash(".main").removeClass("overflow-y-hidden") };
const modalClose	= (m) => { cash("#" + m).modal("hide"); }

const showVisionPlatform	= ref(false);
const showTrainingModal     = ref(false);

const selectTrainingMode = (mode) => {
	Layout.value.selectedTrainingMode	= mode;
	Layout.value.showTrainingMode 		= false;
	showVisionPlatform.value 			= true;
}

const manageTraining = async (modelId) => {
	currentModel.value = { db: await $firebase.getDoc('model/' + modelId, true) }

	showTrainingModal.value = true;
	showVisionPlatform.value = false;
}

const closeTrainingModal = () => {
	currentModel.value 		= false;
	showTrainingModal.value = false;

	reloadDataset();
}

// Initialize all advanced settings defaults (replicates AdvancedSettings.vue logic)
const initializeAdvancedSettingsDefaults = () => {
	// Set taskType based on dataset type
	if (Dataset.value.data.type === 'ANOMALY') {
		advancedSettings.value.taskType = 'anomalyDetection';
		advancedSettings.value.taskModel = 'padim';
		
		// Initialize complete taskConfig with all anomaly detection defaults
		advancedSettings.value.taskConfig = {
			// Shared anomaly options
			pre_trained: true,
			backbone: 'resnet18',
			
			// PADIM-specific options  
			n_features: 100
		};
		
		// Set batch size to 1 for anomalies
		advancedSettings.value.batchSize = 1;
		
	} else if (Dataset.value.data.type === 'MULTILABEL') {
		advancedSettings.value.taskType = 'segmentation';
		advancedSettings.value.taskModel = 'yolov8';
		advancedSettings.value.taskConfig = {
			size: 'Nano'
		};
	} else if (Dataset.value.data.type === 'MULTICLASS') {
		advancedSettings.value.taskType = 'classification';
		advancedSettings.value.taskModel = 'yolov8';
		advancedSettings.value.taskConfig = {
			size: 'Nano'
		};
	} else {
		// imageObjectDetection and other types
		advancedSettings.value.taskType = 'imageObjectDetection';
		advancedSettings.value.taskModel = 'yolov8';
		advancedSettings.value.taskConfig = {
			size: 'Nano'
		};
	}
};

const loadDataset = async () => {
	Dataset.value.data			= props.dataset;
	Dataset.value.tags      	= await $dataset.getTags(props.dataset.id, Dataset.value.data.type == "MULTILABEL" ? true : false, Dataset.value.data.type);
	Dataset.value.tagStats		= await $dataset.getTagStats(props.dataset);
	Dataset.value.localModels   = props.dataset?.models ? props.dataset.models.models : false;

	clipboard.value = new ClipboardJS(".copyButton");

	const { media } = await $dataset.getImages(Dataset.value.data, { limit: 1});

	// Initialize complete advanced settings as if the modal was opened
	initializeAdvancedSettingsDefaults();

	if (media.length) {
		const { url } 			= await $image.getStorageUrl(media[0].uri);
		const { width, height} 	= await $image.getDimensions(url);

		// Apply resolution limits based on dataset type
		if (Dataset.value.data.type === 'ANOMALY') {
			advancedSettings.value.width	= Math.min(width, 512);
			advancedSettings.value.height 	= Math.min(height, 512);
			advancedSettings.value.batchSize = 1; // Default batch size for anomaly detection
		} else {
			advancedSettings.value.width	= Math.min(width, 2048);
			advancedSettings.value.height 	= Math.min(height, 2048);
		}
	} else {
		advancedSettings.value.width	= 0;
		advancedSettings.value.height 	= 0;
		// Set default batch size for anomaly detection even without media
		if (Dataset.value.data.type === 'ANOMALY') {
			advancedSettings.value.batchSize = 1;
		}
	}
}

const reloadDataset = async () => {
	Layout.value.loading = true;

	const { models } = await $dataset.getModels(props.dataset);

	Dataset.value.localModels	= models;
	localModelsMenu.value 		= Dataset.value.localModels;
	localModelsNames.value 		= Dataset.value.localModels.map((model) => model.id);

	Layout.value.loading = false;

	$emit("updateDataset", "models");
}


const loadModel = async (modelId) => {
	const localModel = Dataset.value.localModels.find((m) => m.id == modelId);
	if (!localModel) {
		console.error("Local model not found");
		return;
	}

	console.log("Loading model", localModel);

	if (localModel.status !== 'finished' && localModel.status !== 'error') {
		manageTraining(localModel.id);
		return;
	}

	currentModel.value = await $model.get(localModel.id);

	Layout.value.loading = true;
	try {
		if (localModel.automl) {
			await loadVertexModel(localModel);
		} else if (localModel.aws) {
			await loadAwsModel(localModel);
		} else if (localModel.service === "visionPlatform") {
			await loadVisionPlatformModel(localModel);
		} else {
			currentModel.value = { db: localModel };
		}
	} catch (error) {
		console.error("Error loading model:", error);
	} finally {
		Layout.value.loading = false;
	}

	console.log("Model loaded", currentModel.value.db);
}

const loadVertexModel = async (localModel) => {
	currentModel.value = {};

	const responseModel = await $model.getVertex({ modelID: localModel.automl });
	if (responseModel?.status !== "success") {
		currentModel.value = responseModel.data;

		const vertexDataset = await $model.getVertex({ datasetID: currentDataset.value });
		if (vertexDataset?.status !== "success") {
			currentModel.value.vertex = vertexDataset.response[0];
			if (currentModel.value?.vertex?.dataItems?.[0]?.length) {
				currentModel.value.vertex.dataDivision = { test: 0, training: 0, validation: 0 };
				for (let item of currentModel.value.vertex.dataItems[0]) {
					const mlUseLabel = item.labels["aiplatform.googleapis.com/ml_use"];
					if (mlUseLabel) {
						const key = mlUseLabel.toLowerCase();
						currentModel.value.vertex.dataDivision[key] = (currentModel.value.vertex.dataDivision[key] || 0) + 1;
					}
				}
			}
		} else {
			currentModel.value.errorLoad = '1- ' + vertexDataset?.error;
		}
	} else {
		currentModel.value.errorLoad = '2- ' + responseModel?.error;
	}

	currentModel.value.db = localModel;
	currentModel.value.vertexEvaluation = await $model.renderEvaluations(localModel.id, {
		theme:          "light",
		onlyMetrics:    true,
		titles:         false,
	});
	clipboard.value = new ClipboardJS(".copyButton");
}

const loadAwsModel = async (localModel) => {
	semiSupervisedEvaluationObjects.value = [];

	currentModel.value                  = await $aws.getDataset(localModel.aws);
	currentModel.value.db               = localModel;
	currentModel.value.awsModel         = await $aws.getModel(localModel.aws, localModel.ModelVersion);
	currentModel.value.packageJobs      = await $aws.getModelPackagingJobs(localModel.aws, localModel.ModelVersion);
	currentModel.value.awsEvaluation    = await $aws.getEvaluation(localModel.aws, localModel.ModelVersion);
}

const loadModelPackagingJob = async (packageJob) => {
	currentModel.value.packageJobData = await $aws.getModelPackagingJob(currentModel.value.projectName, packageJob);
}

const deleteModel = async () => {
	cash("#model-delete-box").modal("hide");
	if (Dataset.value.selectedModel) {
		await $model.update(Dataset.value.selectedModel, { deleted: Boolean(true) })
	}
}

const exportModelToBucket = async () => { 
	Layout.value.loading = true;
	if (currentModel.value?.db?.id) {
		await $model.export(currentModel.value.db.id, { updateModel: true }).then(async response =>  {
			if (response?.savedModel) currentModel.value.db.savedModel = response?.savedModel;
		})
	}
	Layout.value.loading = false
}



const loadVisionPlatformModel = async (localModel) => {
	currentModel.value = {};
	currentModel.value.db = localModel;
  
	if (localModel.status === "training" || localModel.status === "pending") {
		return;
	}

	const modelEvaluation = {
		service:        "visionPlatform",
		status:         "finished",
		dataDivision: {
			train:      localModel.dataDivision.train,
			test:       localModel.dataDivision.test,
			validation: localModel.dataDivision.validation,
		},
		imageDataDivision: {
			train:      localModel.imageDataDivision.train,
			test:       localModel.imageDataDivision.test,
			validation: localModel.imageDataDivision.validation,
		},
		configuration: {
			budget:     1000,
			cost:       500,
			stopReason: localModel.stopReason ? localModel.stopReason : "MODEL_CONVERGED",
			createdAt:  localModel.createdAt,
		},
		metrics: {
			precision:  localModel.metrics.all.precision,
			recall:     localModel.metrics.all.recall,
			f1:         localModel.metrics.all.f1score,
		},
	};

	modelEvaluation.render = await $vision.renderEvaluations(localModel, {
		theme:          "light",
		onlyMetrics:    true,
		titles:         false,
	});

	loadAnnotations();

	currentModel.value.db.evaluation = modelEvaluation;
}

const loadAnnotations = async () => {
	Layout.value.annotationsImages = await $vision.loadAnnotations(currentModel.value.db);

	if (Layout.value.annotationsImages && Object.keys(Layout.value.annotationsImages).length) {
		const annotationsArray = Object.values(Layout.value.annotationsImages);

		const batchSize = 5;
		for (let i = 0; i < annotationsArray.length; i += batchSize) {
			const batch = annotationsArray.slice(i, i + batchSize);

			await Promise.all(batch.map((annotation) => {
				return new Promise((resolve) => {
					const img = new Image();
					img.crossOrigin = 'anonymous';
					img.onload = () => {
						const canvas = document.createElement('canvas');
						const ctx = canvas.getContext('2d');

						canvas.width = img.width;
						canvas.height = img.height;
						ctx.drawImage(img, 0, 0);

						if (annotation.data && annotation.data.length) {
							ctx.lineWidth = 4;
							annotation.data.forEach((metadata) => {
								if (!annotation.confidence || annotation.confidence < metadata.confidence) {
									annotation.confidence 	= metadata.confidence;
									annotation.class 		= metadata.class;
								}

								if (!metadata.points || !metadata.points.length) return;

								const rawPointsEntries = Object.entries(metadata.points[0]);

								rawPointsEntries.sort((a, b) => {
									const numA = parseInt(a[0].replace(/\D/g, ''));
									const numB = parseInt(b[0].replace(/\D/g, ''));
									return numA - numB;
								});

								const scaledPoints = rawPointsEntries.map(([, [x, y]]) => [x * img.width, y * img.height]);

								if (scaledPoints.length >= 3) {
									const hexClass = $helper.StringtoHex(metadata.class);
									ctx.strokeStyle = $helper.hexToRgb(hexClass);
									ctx.lineWidth = 4;

									ctx.beginPath();
									ctx.moveTo(scaledPoints[0][0], scaledPoints[0][1]);

									for (let i = 1; i < scaledPoints.length; i++) {
										ctx.lineTo(scaledPoints[i][0], scaledPoints[i][1]);
									}

									ctx.closePath();
									ctx.stroke();
								}
							});
						}

						annotation.cachedUrl = canvas.toDataURL('image/png');
						resolve();
					};

					img.onerror = () => resolve();
					img.src = annotation.url;
				});
			}));

			await new Promise(resolve => setTimeout(resolve, 10));
		}
	}

	Layout.value.annotationsLoaded = true;
}


const closeCurrentModel = () => {
	currentModel.value	= false;
	
	Layout.value.annotationsImages 		= false;
	Layout.value.annotationsLoaded 		= false;
	Layout.value.showAnnotations 		= false;
	Layout.value.showAdvancedSettings 	= false;
	Layout.value.selectedTrainingMode 	= null;
	
	showVisionPlatform.value 	= false;
	showTrainingModal.value 	= false;
}

const toggleAnnotations = () => {
	Layout.value.showAnnotations = !Layout.value.showAnnotations;
}

const openModal = (index)=>  {
	selectedIndex.value = index;
	showModal.value = true;
}

const closeModal = () => {
	showModal.value = false;
	if (canvasInstance) {
		canvasInstance.dispose();
		canvasInstance = null;
	}
}

const prevImage = () => {
	if (selectedIndex.value > 0) {
		selectedIndex.value--;
	}
}

const nextImage = () => {
	if (selectedIndex.value < annotationsList.value.length - 1) {
		selectedIndex.value++;
	}
}



/* ----------------------------------------------------- */

const fabricCanvas = ref(null);
const canvasWrapper = ref(null);

let canvasInstance = null;

const setupFabricCanvas = () => {
	clearCanvas();

	const el = fabricCanvas.value;
	canvasInstance = new fabric.Canvas(el, {
		selection: 				false,
		preserveObjectStacking: true,
		hoverCursor: 			'grab',
	});

	setupCanvasEvents(canvasInstance);
}

const setupCanvasEvents = (canvas) => {
	let isDragging = false;
	let last = { x: 0, y: 0 };

	canvas.on({
		'mouse:wheel': (opt) => {
			const { deltaY, offsetX, offsetY } = opt.e;
			let zoom = canvas.getZoom() * (0.999 ** deltaY);
			canvas.zoomToPoint({ x: offsetX, y: offsetY }, Math.max(0.1, Math.min(zoom, 10)));
			opt.e.preventDefault();
			opt.e.stopPropagation();
		},
		'mouse:down': ({ e }) => {
			isDragging = true;
			canvas.selection = false;
			last = { x: e.clientX, y: e.clientY };
		},
		'mouse:move': ({ e }) => {
			if (!isDragging) return;
			const vpt = canvas.viewportTransform;
			vpt[4] += e.clientX - last.x;
			vpt[5] += e.clientY - last.y;
			last = { x: e.clientX, y: e.clientY };
			canvas.requestRenderAll();
		},
		'mouse:up': () => {
			isDragging = false;
		}
	});
}

const clearCanvas = () => {
	if (canvasInstance) {
		canvasInstance.clear();
	}
}

/* const renderAnnotation = (annotation) => {
	Layout.value.loadingAnnotation = true;

	clearCanvas();

	fabric.Image.fromURL(annotation.url, (img) => {
		img.set({ selectable: false });
		
		const wrapper = canvasWrapper.value;
		const wrapperWidth = wrapper.clientWidth;
		const wrapperHeight = wrapper.clientHeight;

		canvasInstance.setWidth(wrapperWidth);
		canvasInstance.setHeight(wrapperHeight);

		// Escala para que la imagen encaje completamente
		const scaleX = wrapperWidth / img.width;
		const scaleY = wrapperHeight / img.height;
		const zoom = Math.min(scaleX, scaleY);

		// Centrar imagen
		const left = (wrapperWidth - img.width * zoom) / 2;
		const top = (wrapperHeight - img.height * zoom) / 2;

		canvasInstance.setZoom(zoom);
		canvasInstance.viewportTransform[4] = left;
		canvasInstance.viewportTransform[5] = top;

		canvasInstance.add(img);
		canvasInstance.sendToBack(img);

		// Dibujar anotaciones escaladas al zoom
		(annotation.data || []).forEach((metadata) => {
			const entries = Object.entries(metadata.points[0])
				.sort((a, b) => {
					const numA = parseInt(a[0].replace(/\D/g, ''));
					const numB = parseInt(b[0].replace(/\D/g, ''));
					return numA - numB;
				})
				.map(([, [x, y]]) => ({
					x: x * img.width,
					y: y * img.height
				}));

			if (entries.length >= 3) {
				const polygon = new fabric.Polygon(entries, {
					stroke: $helper.hexToRgb($helper.StringtoHex(metadata.class)),
					strokeWidth: 3 / zoom,
					fill: 'transparent',
					selectable: false
				});
				canvasInstance.add(polygon);

				// Obtener el centroide para posicionar el texto
				const center = polygon.getCenterPoint();
				const label = new fabric.Text(metadata.class, {
					left: center.x,
					top: center.y,
					fill: '#ffffff',
					fontSize: 8 / zoom,
					originX: 'center',
					originY: 'center',
					backgroundColor: 'rgba(0,0,0,0.5)',
					selectable: false
				});
				canvasInstance.add(label);
			}
		});


		canvasInstance.renderAll();
		Layout.value.loadingAnnotation = false;
	}, { crossOrigin: 'anonymous' });
} */


const renderAnnotation = (annotation) => {
	Layout.value.loadingAnnotation = true;
	clearCanvas();

	loadImage(annotation.url).then((img) => {
		setupCanvasWithImage(img);
		drawAnnotations(annotation.data || [], img);
		canvasInstance.renderAll();
		Layout.value.loadingAnnotation = false;
	});
};

const loadImage = (url) => {
	return new Promise((resolve) => {
		fabric.Image.fromURL(url, (img) => {
			img.set({ selectable: false });
			resolve(img);
		}, { crossOrigin: 'anonymous' });
	});
};

const setupCanvasWithImage = (img) => {
	const wrapper = canvasWrapper.value;
	const wrapperWidth = wrapper.clientWidth;
	const wrapperHeight = wrapper.clientHeight;

	canvasInstance.setWidth(wrapperWidth);
	canvasInstance.setHeight(wrapperHeight);

	const { zoom, left, top } = getZoomAndCenter(wrapperWidth, wrapperHeight, img.width, img.height);

	canvasInstance.setZoom(zoom);
	canvasInstance.viewportTransform[4] = left;
	canvasInstance.viewportTransform[5] = top;

	canvasInstance.add(img);
	canvasInstance.sendToBack(img);
};

const getZoomAndCenter = (containerW, containerH, imageW, imageH) => {
	const zoom = Math.min(containerW / imageW, containerH / imageH);
	const left = (containerW - imageW * zoom) / 2;
	const top = (containerH - imageH * zoom) / 2;
	return { zoom, left, top };
};

const drawAnnotations = (annotations, img) => {
	const zoom = canvasInstance.getZoom();

	annotations.forEach((metadata) => {
		if (!metadata.points || !metadata.points.length) return;
		const points = extractPoints(metadata.points[0], img.width, img.height);

		if (points.length >= 3) {
			const polygon = createPolygon(points, metadata.class, zoom);
			canvasInstance.add(polygon);

			const label = createLabel(metadata.class, polygon, zoom);
			canvasInstance.add(label);
		}
	});
};

const extractPoints = (pointObj, imgW, imgH) => {
	return Object.entries(pointObj)
		.sort(([a], [b]) => {
			const numA = parseInt(a.replace(/\D/g, ''));
			const numB = parseInt(b.replace(/\D/g, ''));
			return numA - numB;
		})
		.map(([, [x, y]]) => ({
			x: x * imgW,
			y: y * imgH
		}));
};

const createPolygon = (points, className, zoom) => {
	return new fabric.Polygon(points, {
		stroke: $helper.hexToRgb($helper.StringtoHex(className)),
		strokeWidth: 3 / zoom,
		fill: 'transparent',
		selectable: false
	});
};

const createLabel = (text, polygon, zoom) => {
	const center = polygon.getCenterPoint();
	return new fabric.Text(text, {
		left: center.x,
		top: center.y,
		fill: '#ffffff',
		fontSize: 8 / zoom,
		originX: 'center',
		originY: 'center',
		backgroundColor: 'rgba(0,0,0,0.5)',
		selectable: false
	});
};


/* ----------------------------------------------------- */



const loadEvaluationObjects = async (objs) => {
	currentModel.value.awsEvaluation.loading             = true;
	if (!currentModel.value.awsEvaluation.sizeObject) currentModel.value.awsEvaluation.sizeObject = 50;
	semiSupervisedEvaluationObjects.value                = [];
	currentModel.value.awsEvaluation.order               = currentModel.value.awsEvaluation.order ? currentModel.value.awsEvaluation.order : "asc";
	currentModel.value.awsEvaluation.filterPrediction    = currentModel.value.awsEvaluation.filterPrediction ? currentModel.value.awsEvaluation.filterPrediction : "all";
	
	if (!currentModel.value.awsEvaluation.oriAwsTestObjects) {
		currentModel.value.awsEvaluation.oriAwsTestObjects = [];
		for (let i=0; i<objs.length; i++) { 
			if (objs[i].key) {
				currentModel.value.awsEvaluation.oriAwsTestObjects.push({ imageSrc: await $aws.getObject(objs[i].key, true), obj: objs[i]  });
			}
		}
	}
	
	let modelTestObj = currentModel.value.awsEvaluation.oriAwsTestObjects;
	
	if (modelTestObj.length) {
		let filterObjects = [];

		for (let i = 0; i < modelTestObj.length; i++) {
			let addImage = true;
			let awsObj   = modelTestObj[i];
			
			if (awsObj?.obj && currentModel.value.awsEvaluation.filterPrediction != "all") {
				if (currentModel.value.awsEvaluation.filterPrediction=="correct"   && awsObj?.obj['anomaly-label']!=awsObj?.obj['anomaly-label-detected']) addImage = false;
				if (currentModel.value.awsEvaluation.filterPrediction=="incorrect" && awsObj?.obj['anomaly-label']==awsObj?.obj['anomaly-label-detected']) { addImage = false; filterObjects.push({ empty: true}); }
			}
			if (addImage) filterObjects.push(awsObj)
		}

		modelTestObj = filterObjects;

		if (!modelTestObj[0]['empty']) {
			if (currentModel.value.awsEvaluation.order == "asc") modelTestObj.sort((a, b) => a.obj['anomaly-label-detected-metadata']['confidence'] - b.obj['anomaly-label-detected-metadata']['confidence']);
			else modelTestObj.sort((a, b) => b.obj['anomaly-label-detected-metadata']['confidence'] - a.obj['anomaly-label-detected-metadata']['confidence']);
		}
	}

	semiSupervisedEvaluationObjects.value = modelTestObj;
	currentModel.value.awsEvaluation.loading = false;
}

const linkToBrowser = () => { 
	cash(".modal").modal("hide");
	Layout.value.modalImport = false; 
	router.push({ name: 'side-menu-datasets', params: { datasetID: props.dataset.id } });  
}

const copyToClipboard = async () => { 
	clipboard.value.on('success', () => { $helper.NotificationTxt({ text: "Storage copied to clipboard ", position: "center" });  });
	clipboard.value.on('error', () => {   $helper.NotificationTxt({ text: "Storage could not copy text", position: "center" });  });
}

const removeHyphens = () => {
	vpTrainConfig.value.modelName = vpTrainConfig.value.modelName.replace(/-/g, '_').toLowerCase();
}



onMounted(async () => {
	console.log("Mounted Models");

	Layout.value.loading = true;

	currentModel.value   = false;
	currentDataset.value = props.dataset.id;

	await loadDataset();

	Layout.value.loading = false;

	localModelsMenu.value	= Dataset.value.localModels;
	localModelsNames.value 	= Dataset.value.localModels.map((model) => model.id);
});

watch(() => props.dataset, async () => { 
	currentDataset.value = props.dataset.id; 
	await loadDataset(); 
});

watch(() => vpTrainConfig.value.method, async () => {
	Dataset.value.validateToTrain = false;
	let validateOpt = { noDivision: vpTrainConfig.value.method == 'auto' ? true : false };
	if (vpTrainConfig.value.tagMap) validateOpt.tagMap = vpTrainConfig.value.tagMap;
	Dataset.value.validateToTrain = await $model.validateToTrain(Dataset.value, validateOpt);
});

watch(() => showModal.value, async (visible) => {
	if (visible && currentAnnotation.value) {
		await nextTick();
		setupFabricCanvas();
		renderAnnotation(currentAnnotation.value);
	}
});

watch(() => currentAnnotation, (annotation) => {
	if (annotation && canvasInstance) {
		renderAnnotation(annotation);
	}
}, { immediate: true });

watch(() => selectedIndex.value, async (index) => {
	if (index >= 0 && index < annotationsList.value.length) {
		await nextTick();
		renderAnnotation(annotationsList.value[index]);
	}
}, { immediate: true });
</script>

<style>
.overflow-hidden { 
	overflow: hidden !important; 
}

.disabledButton {
	cursor: not-allowed !important;
	-webkit-touch-callout: none;
	-webkit-user-select: none;
	-khtml-user-select: none;
	-moz-user-select: none;
	-ms-user-select: none;
	user-select: none;
}

#model-supervised-box, 
#model-semi-supervised-box, 
#model-vision-platform-box { 
	background-color: #f2f3f3 !important;
}

#model-supervised-box .modal-dialog, 
#model-semi-supervised-box .modal-dialog,
#model-vision-platform-box .modal-dialog { 
	min-width: 900px !important; 
}

#model-supervised-box .nav.nav-tabs .active, 
#model-semi-supervised-box .nav.nav-tabs .active
#model-vision-platform-box .nav.nav-tabs .active {
	--tw-border-opacity: 1;
	background-color: #1a73e8;
	border-color: #1a73e8;
	font-weight: 500;
	color: #fff;
}

#rpDialogBox { 
	position: fixed; 
	top: 40px; 
	left: calc( (100% - 600px) /2); 
	z-index: 999; 
	width: c;
}

.rpoverlayDialog { 
	position: fixed; 
	top: 0; 
	left: 0; 
	right: 0; 
	bottom: 0; 
	z-index: 999; 
	background-color: rgba(0, 0, 0, 0.8); 
}

div.scroll-container {
	overflow-x: auto;
	padding: 10px;
}

div.images-wrapper {
	white-space: nowrap;
}

.image-item {
	display: inline-block;
	margin-right: 10px;
	justify-content: center;
}

.metricsButton {
	width: 12rem;
	border-radius: 9999px;
	font-size: 0.75rem;
	font-weight: 400;
	text-align: center;
	border: 1px solid var(--primary-100);
	color: var(--primary-700);
	cursor: pointer;
	display: flex;
	align-items: center;
	justify-content: center;
	float: right;
	margin-left: auto;
	margin-right: 0.5rem;
}

.metricsButton.hover-enabled:hover {
	background-color: var(--primary-700);
	color: #FFFFFF;
}

.metricsButton:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

.model-box {
	padding: 1rem;
	display: flex;
	align-items: center;
	cursor: pointer;
	border-width: 1px;
}
</style>